import {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from 'react';
import axios from 'axios';

interface LoginResponse {
  token: string;
  user: User;
}

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  user: User | null;
  token: string | null;
  login: (loginResponse: LoginResponse) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  loading: true,
  token: null,
  user: null,
  login: () => {},
  logout: () => {},
});

const getBackendBaseUrl = () =>
  (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '');

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const login = (loginResponse: LoginResponse) => {
    localStorage.setItem('strong_auth_token', loginResponse.token);
    localStorage.setItem('strong_user_data', JSON.stringify(loginResponse.user));

    setUser(loginResponse.user);
    setToken(loginResponse.token);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('strong_auth_token');
    localStorage.removeItem('strong_user_data');
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('strong_auth_token');
    const storedUser = localStorage.getItem('strong_user_data');

    const validateStoredSession = async () => {
      if (!storedToken || !storedUser) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${getBackendBaseUrl()}/api/users/cms_auth`, {
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        });

        if (Number(response.data?.role) !== 1) {
          throw new Error('CMS access denied');
        }

        localStorage.setItem('strong_user_data', JSON.stringify(response.data));
        setToken(storedToken);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (_error) {
        localStorage.removeItem('strong_auth_token');
        localStorage.removeItem('strong_user_data');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    validateStoredSession();
  }, []);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, loading, user, token, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
